import { create } from "zustand";
import { auth } from '../../../../auth';
import convex, { waitForConvexAuthReady } from "../../../../convexClient";
import { api } from "../../../../convex/_generated/api";
import { withActiveTenant } from "../../../../utils/tenantUtils";

const normalizeAsset = (doc) => ({
  id: doc._id || doc.id,
  name: doc?.name ?? "",
  imageUrl: doc?.imageUrl ?? "",
  purchaseDate: doc?.purchaseDate ?? "",
  purchaseAmount: doc?.purchaseAmount ?? 0,
  currentStatus: doc?.currentStatus ?? "Good Condition",
  statusDate: doc?.statusDate ?? "",
  statusHistory: Array.isArray(doc?.statusHistory) ? doc.statusHistory : [],
  userId: doc?.userId,
  archived: doc?.archived ?? false,
  metadata: {
    userId: doc?.metadata?.userId,
    createdAt: doc?.metadata?.createdAt ?? null,
    updatedAt: doc?.metadata?.updatedAt ?? null,
  },
  sharedWith: Array.isArray(doc?.sharedWith) ? doc.sharedWith : [],
});

const useAssetStore = create((set, get) => ({
  assets: [],
  loading: false,
  error: null,

  fetchAssets: async () => {
    const user = auth.currentUser;
    if (!user) { set({ assets: [], error: "Not authenticated" }); return; }
    set({ loading: true, error: null });
    try {
      await waitForConvexAuthReady();
      const docs = await convex.query(api.assets.listAssets, withActiveTenant());
      set({ assets: docs.map(normalizeAsset), loading: false });
    } catch (e) {
      set({ loading: false, error: e.message || "Failed to fetch assets" });
    }
  },

  createAsset: async (payload) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");

    const now = new Date().toISOString();
    const initialHistory = {
      status: payload.currentStatus ?? "Good Condition",
      statusDate: payload.statusDate ?? "",
      timestamp: now,
      note: "Asset created",
    };

    const id = await convex.mutation(api.assets.createAsset, withActiveTenant({
      name: payload.name?.trim() ?? "",
      imageUrl: payload.imageUrl?.trim() ?? "",
      purchaseDate: payload.purchaseDate ?? "",
      purchaseAmount: parseFloat(payload.purchaseAmount) || 0,
      currentStatus: payload.currentStatus ?? "Good Condition",
      statusDate: payload.statusDate ?? "",
      statusHistory: [initialHistory],
      sharedWith: Array.isArray(payload.sharedWith) ? payload.sharedWith : [],
      ...(payload.taskId ? { taskId: payload.taskId } : {}),
    }));

    const newAsset = normalizeAsset({
      _id: id, ...payload,
      statusHistory: [initialHistory],
      userId: user.uid,
      metadata: { userId: user.uid, createdAt: Date.now(), updatedAt: Date.now() },
    });

    set((s) => ({ assets: [newAsset, ...s.assets] }));
    return id;
  },

  updateAsset: async (id, updates) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");

    const currentAsset = get().assets.find((a) => a.id === id);
    const updateData = { ...updates };

    if (updates.currentStatus && currentAsset && updates.currentStatus !== currentAsset.currentStatus) {
      const historyEntry = {
        status: updates.currentStatus,
        statusDate: updates.statusDate ?? "",
        timestamp: new Date().toISOString(),
        note: updates.statusNote ?? `Status changed to ${updates.currentStatus}`,
      };
      updateData.statusHistory = [...(currentAsset.statusHistory || []), historyEntry];
    }
    delete updateData.statusNote;

    await convex.mutation(api.assets.updateAsset, { id, ...updateData });

    set((s) => ({
      assets: s.assets.map((a) =>
        a.id === id
          ? {
              ...a,
              ...updates,
              statusHistory: updateData.statusHistory || a.statusHistory,
              metadata: { ...a.metadata, updatedAt: Date.now() },
            }
          : a
      ),
    }));
  },

  archiveAsset: async (id, archived = true) => {
    await convex.mutation(api.assets.updateAsset, { id, archived });
    set((s) => ({
      assets: s.assets.map((a) => (a.id === id ? { ...a, archived } : a)),
    }));
  },

  deleteAsset: async (id) => {
    await convex.mutation(api.assets.deleteAsset, { id });
    set((s) => ({ assets: s.assets.filter((a) => a.id !== id) }));
  },
}));

export default useAssetStore;
